import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate, maskMoneyInput, moneyToInput, parseInputToApi } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import { CategorySelect } from '@/features/categories/CategorySelect';

type Recurrence = {
  id: number;
  description: string;
  type: 'receita' | 'despesa';
  amount: MoneyString;
  frequency: string;
  interval: number;
  day_of_month: number | null;
  starts_at: string;
  ends_at: string | null;
  status: string;
  entity_id: number;
  entity_name: string | null;
  category_id: number | null;
  category_name: string | null;
  account_id: number | null;
  account_name: string | null;
  party_id: number | null;
  party_name: string | null;
  is_essential: boolean;
  materialized_until: string | null;
  occurrences_generated: number;
  next_due_date: string | null;
};

const FREQUENCIES: Record<string, string> = {
  diaria: 'Diária',
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  dia_util: 'N-ésimo dia útil',
};

export function RecurrencesPage({ selectedIds }: { selectedIds: number[] }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Recurrence | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['recurrences', selectedIds],
    queryFn: () => api.get<{ data: Recurrence[] }>('/recurrences', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const end = useMutation({
    mutationFn: (id: number) => api.delete(`/recurrences/${id}`, { entityIds: selectedIds }),
    onSuccess: () => refreshAll(),
  });

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: ['recurrences'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const active = list.data?.filter((r) => r.status === 'ativa') ?? [];

  // Total mensal aproximado: só o que é efetivamente mensal entra na conta,
  // porque somar uma anual com uma mensal daria um número sem significado.
  const monthly = active.filter((r) => r.frequency === 'mensal');
  const monthlyExpense = monthly
    .filter((r) => r.type === 'despesa')
    .reduce((s, r) => s + Number(r.amount), 0);
  const monthlyIncome = monthly
    .filter((r) => r.type === 'receita')
    .reduce((s, r) => s + Number(r.amount), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Recorrências</h1>
        <button
          onClick={() => setFormOpen(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Nova recorrência
        </button>
      </div>

      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Cadastre uma vez o que se repete — aluguel, salário, assinaturas — e o sistema gera os
        lançamentos dos próximos meses. Eles já entram na previsão de caixa.
      </p>

      {monthly.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-900">
          <span>
            <span className="text-slate-500 dark:text-slate-400">Receita mensal fixa: </span>
            <strong className="text-emerald-700 dark:text-emerald-400">
              {formatMoney(monthlyIncome.toFixed(4))}
            </strong>
          </span>
          <span>
            <span className="text-slate-500 dark:text-slate-400">Despesa mensal fixa: </span>
            <strong className="text-rose-700 dark:text-rose-400">
              {formatMoney(monthlyExpense.toFixed(4))}
            </strong>
          </span>
          <span>
            <span className="text-slate-500 dark:text-slate-400">Sobra mensal: </span>
            <strong
              className={
                monthlyIncome - monthlyExpense >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-rose-600 dark:text-rose-400'
              }
            >
              {formatMoney((monthlyIncome - monthlyExpense).toFixed(4))}
            </strong>
          </span>
          <span className="ml-auto text-xs text-slate-400">
            considera apenas as {monthly.length} recorrências mensais
          </span>
        </div>
      )}

      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {list.isPending ? (
          <div className="animate-pulse p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mb-2 h-9 rounded bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-sm text-rose-600">Erro ao carregar.</p>
        ) : (list.data?.length ?? 0) === 0 ? (
          <div className="p-10 text-center">
            <p className="font-medium">Nenhuma recorrência cadastrada</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              É o que mais reduz digitação: a maior parte dos lançamentos do mês se repete.
            </p>
            <button
              onClick={() => setFormOpen(true)}
              className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              + Nova recorrência
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2.5 font-medium">Descrição</th>
                <th className="px-3 py-2.5 font-medium">Frequência</th>
                <th className="px-3 py-2.5 font-medium">Cliente / Fornecedor</th>
                <th className="px-3 py-2.5 font-medium">Entidade</th>
                <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                <th className="px-3 py-2.5 font-medium">Próxima</th>
                <th className="px-3 py-2.5 font-medium">Geradas</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                    r.status !== 'ativa' ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-3 py-2.5">
                    <span className="font-medium">{r.description}</span>
                    {r.is_essential && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                        essencial
                      </span>
                    )}
                    {r.category_name && (
                      <span className="ml-1.5 text-xs text-slate-400">{r.category_name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">
                    {FREQUENCIES[r.frequency] ?? r.frequency}
                    {r.day_of_month && (
                      <span className="text-xs text-slate-400"> · dia {r.day_of_month}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">
                    {r.party_name ?? <span className="text-slate-300 dark:text-slate-600">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">{r.entity_name}</td>
                  <td
                    className={`whitespace-nowrap px-3 py-2.5 text-right font-medium ${
                      r.type === 'receita'
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-rose-700 dark:text-rose-400'
                    }`}
                  >
                    {r.type === 'receita' ? '+' : '−'} {formatMoney(r.amount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    {r.next_due_date ? formatDate(r.next_due_date) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
                    {r.occurrences_generated}
                    {r.materialized_until && (
                      <span className="ml-1 text-xs text-slate-400">
                        até {formatDate(r.materialized_until)}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    {r.status === 'ativa' && (
                      <button
                        onClick={() => setEditing(r)}
                        className="mr-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
                      >
                        Editar
                      </button>
                    )}

                    {r.status === 'ativa' && (
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Encerrar "${r.description}"?\n\nAs ocorrências futuras ainda não pagas serão removidas. ` +
                                'As já pagas permanecem — apagá-las mudaria saldos de períodos fechados.',
                            )
                          ) {
                            end.mutate(r.id);
                          }
                        }}
                        className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-rose-500 hover:text-rose-600 dark:border-slate-700"
                      >
                        Encerrar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(formOpen || editing) && reference.data && (
        <RecurrenceForm
          /*
            `key` remonta o formulário ao trocar de recorrência.

            Sem ele, abrir a segunda recorrência reaproveitaria a instância e os
            `useState` inicializados com a primeira — a tela mostraria os dados
            de um cadastro e salvaria no outro.
          */
          key={editing?.id ?? 'nova'}
          recurrence={editing}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={(note) => {
            setFormOpen(false);
            setEditing(null);
            if (note) window.alert(note);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function RecurrenceForm({
  recurrence,
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  recurrence: Recurrence | null;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: (note?: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = recurrence !== null;

  const [type, setType] = useState<'receita' | 'despesa'>(recurrence?.type ?? 'despesa');
  const [description, setDescription] = useState(recurrence?.description ?? '');
  const [entityId, setEntityId] = useState(recurrence?.entity_id ?? reference.entities[0]?.id ?? 0);
  const [amountMasked, setAmountMasked] = useState(
    recurrence ? moneyToInput(recurrence.amount) : '',
  );
  const [frequency, setFrequency] = useState(recurrence?.frequency ?? 'mensal');
  const [dayOfMonth, setDayOfMonth] = useState<number | ''>(
    recurrence?.day_of_month ?? new Date().getDate(),
  );
  const [startsAt, setStartsAt] = useState(recurrence?.starts_at?.slice(0, 10) ?? today);
  const [endsAt, setEndsAt] = useState(recurrence?.ends_at?.slice(0, 10) ?? '');
  const [categoryId, setCategoryId] = useState(recurrence?.category_id?.toString() ?? '');
  const [accountId, setAccountId] = useState(recurrence?.account_id?.toString() ?? '');
  const [partyId, setPartyId] = useState(recurrence?.party_id?.toString() ?? '');
  const [isEssential, setIsEssential] = useState(recurrence?.is_essential ?? false);

  /**
   * De quando vale a alteração — a decisão central da edição.
   *
   * `toda_a_serie` reescreve o modelo: serve para conserto, quando o cadastro
   * estava errado desde o começo.
   *
   * `esta_e_futuras` encerra a série atual e abre outra a partir de hoje:
   * serve para MUDANÇA, quando o aluguel realmente subiu. Preserva o histórico
   * explicável — até julho eram R$ 2.800, de agosto em diante são R$ 3.100, e
   * os dois valores continuam certos nos seus períodos.
   *
   * Escolher o modo errado não perde dado, mas conta a história errada, e por
   * isso a tela pergunta em vez de decidir.
   */
  const [mode, setMode] = useState<'toda_a_serie' | 'esta_e_futuras'>('toda_a_serie');

  // Contatos vêm de `/parties`: o endpoint de referência não os traz, porque a
  // lista pode ser longa demais para entrar num payload carregado em toda tela.
  const partyList = useQuery({
    queryKey: ['parties', 'options', selectedIds],
    queryFn: () =>
      api.get<{ data: Array<{ id: number; name: string }> }>('/parties?per_page=200', {
        entityIds: selectedIds,
      }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const parties = partyList.data ?? [];

  const amount = parseInputToApi(amountMasked);
  const isMonthly = ['mensal', 'bimestral', 'trimestral', 'semestral', 'anual'].includes(frequency);
  const accounts = reference.accounts.filter((a) => a.entity_id === entityId);
  const categories = reference.categories.filter(
    (c) => c.selectable && (c.type === type || c.type === 'ambos'),
  );

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        /**
         * A edição manda só `changes`, e só o que MUDOU.
         *
         * Entidade, tipo e início não vão: mudar a entidade de uma série já
         * lançada moveria lançamentos entre PF e PJ sem nenhum rastro, e mudar
         * o início mexeria em ocorrências que já existem. Quem precisa disso
         * encerra a série e cadastra outra — que é o registro honesto do que
         * aconteceu.
         */
        const changes: Record<string, unknown> = {};

        if (description.trim() !== recurrence.description) changes.description = description.trim();
        if (Number(amount) !== Number(recurrence.amount)) changes.amount = amount;
        if (frequency !== recurrence.frequency) changes.frequency = frequency;
        if (isEssential !== recurrence.is_essential) changes.is_essential = isEssential;

        const day = isMonthly && dayOfMonth !== '' ? Number(dayOfMonth) : null;
        if (day !== recurrence.day_of_month) changes.day_of_month = day;

        const cat = categoryId === '' ? null : Number(categoryId);
        if (cat !== recurrence.category_id) changes.category_id = cat;

        const acc = accountId === '' ? null : Number(accountId);
        if (acc !== recurrence.account_id) changes.account_id = acc;

        const par = partyId === '' ? null : Number(partyId);
        if (par !== recurrence.party_id) changes.party_id = par;

        return api.patch<{ meta?: { note?: string } }>(
          `/recurrences/${recurrence.id}`,
          { mode, changes },
          { entityIds: selectedIds },
        );
      }

      return api.post<{ meta?: { note?: string } }>('/recurrences', {
        description,
        type,
        amount,
        entity_id: entityId,
        frequency,
        ...(isMonthly && dayOfMonth !== '' ? { day_of_month: Number(dayOfMonth) } : {}),
        starts_at: startsAt,
        ...(endsAt ? { ends_at: endsAt } : {}),
        ...(categoryId ? { category_id: Number(categoryId) } : {}),
        ...(accountId ? { account_id: Number(accountId) } : {}),
        ...(partyId ? { party_id: Number(partyId) } : {}),
        is_essential: isEssential,
      }, { entityIds: selectedIds });
    },
    onSuccess: (res) => onSaved(res?.meta?.note),
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const canSubmit = description.trim().length >= 2 && Number(amount) > 0 && entityId > 0;

  return (
    <Modal title={isEdit ? 'Editar recorrência' : 'Nova recorrência'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        {/*
          Receita/despesa NÃO muda na edição.

          Virar o sinal de uma série já lançada inverteria receitas e despesas
          de meses fechados, e o DRE de um período conciliado mudaria sozinho.
          Quem cadastrou do lado errado encerra e cadastra de novo — que é o
          registro honesto do que aconteceu.
        */}
        <div className="flex gap-2">
          {(['despesa', 'receita'] as const).map((t) => (
            <button
              key={t}
              type="button"
              disabled={isEdit}
              onClick={() => { setType(t); setCategoryId(''); }}
              className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                type === t
                  ? t === 'receita'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                    : 'border-rose-500 bg-rose-50 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
                  : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400'
              } ${isEdit ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {t === 'receita' ? '↓ Receita' : '↑ Despesa'}
            </button>
          ))}
        </div>

        <Field label="Descrição" required>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            minLength={2}
            autoFocus
            placeholder="Ex.: Aluguel do apartamento"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor" required>
            <input
              value={amountMasked}
              onChange={(e) => setAmountMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full text-right"
            />
          </Field>
          {/* Trocar a entidade moveria lançamentos já feitos entre PF e PJ,
              sem rastro nenhum. */}
          <Field label="Entidade" required hint={isEdit ? 'não muda depois de criada' : undefined}>
            <select
              value={entityId}
              disabled={isEdit}
              onChange={(e) => { setEntityId(Number(e.target.value)); setAccountId(''); }}
              className="input w-full disabled:opacity-60"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Frequência" required>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="input w-full"
            >
              {Object.entries(FREQUENCIES).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </Field>
          {isMonthly && (
            <Field label="Dia do mês" hint="dia 31 cai no último dia dos meses curtos">
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value === '' ? '' : Number(e.target.value))}
                className="input w-full text-right"
              />
            </Field>
          )}
        </div>

        {/*
          Vigência só na criação.

          Mexer no início reescreveria ocorrências que já existem — inclusive
          pagas. Para encerrar antes do previsto existe o botão "Encerrar" na
          listagem, que preserva o histórico em vez de reescrevê-lo.
        */}
        {!isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Início" required>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                required
                className="input w-full"
              />
            </Field>
            <Field label="Fim" hint="deixe vazio para indeterminado">
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="input w-full"
              />
            </Field>
          </div>
        )}

        {/*
          Cliente/fornecedor — RF-REC-02.

          A coluna e o vínculo já existiam no banco, e o materializador já
          copiava o contato para cada ocorrência gerada. Faltava o campo na
          tela: sem ele, "Aluguel" não dizia para quem se paga, e o relatório
          por fornecedor perdia justamente as despesas mais previsíveis do mês.
        */}
        <Field
          label={type === 'receita' ? 'Cliente' : 'Fornecedor'}
          hint="vai junto em cada lançamento gerado"
        >
          <select
            value={partyId}
            onChange={(e) => setPartyId(e.target.value)}
            className="input w-full"
          >
            <option value="">Nenhum</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {parties.length === 0 && !partyList.isPending && (
            <p className="mt-1 text-xs text-slate-400">
              Nenhum contato ainda — cadastre no menu <strong>Clientes e fornecedores</strong>.
            </p>
          )}
        </Field>

        <Field label="Categoria">
          <CategorySelect
            value={categoryId}
            onChange={setCategoryId}
            type={type}
            categories={categories}
          />
        </Field>

        <Field label="Conta">
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="input w-full"
          >
            <option value="">Definir na baixa</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>

        <label className="flex cursor-pointer items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={isEssential}
            onChange={(e) => setIsEssential(e.target.checked)}
            className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
          />
          <span>
            Despesa essencial
            <span className="block text-xs text-slate-400">
              entra na priorização quando o caixa aperta
            </span>
          </span>
        </label>

        {isEdit ? (
          /*
            De quando vale a alteração — a pergunta central da edição.

            As duas respostas produzem históricos DIFERENTES e as duas são
            legítimas, então a tela pergunta em vez de decidir. Escolher errado
            não perde dado: conta a história errada, que num sistema financeiro
            é o mesmo estrago com outro nome.
          */
          <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              A partir de quando vale
            </p>

            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  checked={mode === 'toda_a_serie'}
                  onChange={() => setMode('toda_a_serie')}
                  className="mt-0.5 border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                />
                <span>
                  Estava errado — corrigir a série inteira
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    Refaz as ocorrências futuras e corrige também as vencidas ainda em aberto,
                    mantendo o vencimento delas. As já pagas não são tocadas.
                  </span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  checked={mode === 'esta_e_futuras'}
                  onChange={() => setMode('esta_e_futuras')}
                  className="mt-0.5 border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                />
                <span>
                  Mudou agora — valer daqui para frente
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    Encerra a série atual hoje e abre outra com os novos dados. É o caso do
                    aluguel que subiu: até ontem valia um valor, de hoje em diante vale outro, e
                    os dois continuam certos nos seus períodos.
                  </span>
                </span>
              </label>
            </div>

            {mode === 'toda_a_serie' && (
              <p className="mt-2.5 rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                Use com cuidado se a série já corre há meses: corrigir tudo apaga a diferença
                entre o que era cobrado antes e o que passou a ser cobrado.
              </p>
            )}
          </div>
        ) : (
          <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
            Ao salvar, os lançamentos dos próximos 12 meses são gerados e já aparecem na listagem e
            na previsão. Cada um pode ser editado ou baixado individualmente.
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending
              ? 'Salvando…'
              : isEdit
                ? mode === 'esta_e_futuras'
                  ? 'Aplicar daqui para frente'
                  : 'Corrigir a série'
                : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
